import { useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { UserCheck, Check, Search, ChevronLeft, ChevronRight, QrCode, Printer, X, Trophy } from 'lucide-react';
import type { AttendanceLeaderboard, CheckInResult, CheckInStreak, DayRegister } from '@techsofit/api-types';
import { Button, Card, Modal, Skeleton, EmptyState, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { StreakWeekCard } from '../components/StreakWeekCard';
import { CheckInLeaderboard } from '../components/CheckInLeaderboard';
import { useGymData } from '../data/GymDataProvider';
import { useAsync } from '../lib/useAsync';
import { getAttendance, getAttendanceLeaderboard, listMembers } from '../lib/gymApi';
import { hasGymAccess } from '../lib/membership';
import { staffCheckIn } from '../lib/staffCheckIn';
import { todayIso } from '../lib/format';

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoDate(d);
}
function time(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
const DAY_FMT = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

type Tab = 'register' | 'leaderboard';

/** Staff attendance register: who showed up today, mark members present, and the gym's check-in QR. */
export function AttendancePage() {
  const asOf = todayIso();
  const { settings } = useGymData();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get('tab') === 'leaderboard' ? 'leaderboard' : 'register');
  const [date, setDate] = useState(todayIso);
  const [q, setQ] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [justIn, setJustIn] = useState<CheckInResult | null>(null);

  const { data: reg, loading, reload } = useAsync<DayRegister>(() => getAttendance(date), [date]);
  const { data: membersPage } = useAsync(() => listMembers({ size: 1000 }));
  const members = membersPage?.items ?? [];
  const confirm = useConfirm();

  const isToday = date === asOf;
  const checkedIn = useMemo(() => new Map((reg?.visits ?? []).map((v) => [v.memberId, v])), [reg]);

  const roster = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? members.filter((m) => m.fullName.toLowerCase().includes(needle)) : members;
  }, [members, q]);

  async function check(member: (typeof members)[number]) {
    try {
      const result = await staffCheckIn(member, confirm);
      if (!result) return;
      setJustIn(result);
    } catch {
      return;
    }
    reload();
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={tab === 'register' && reg ? `${reg.count} in` : undefined}
        title="Attendance"
        description="Mark members present when they arrive, or let them scan the check-in QR. Streaks and the leaderboard keep people showing up."
        actions={
          <Button variant="outline" onClick={() => setQrOpen(true)}>
            <QrCode className="size-4" aria-hidden /> <span className="hidden sm:inline">Check-in QR</span>
          </Button>
        }
      />

      <div className="mb-4 inline-flex gap-1 rounded-pill bg-surface p-1">
        {(
          [
            { key: 'register', label: 'Check-ins', icon: UserCheck },
            { key: 'leaderboard', label: 'Leaderboard', icon: Trophy },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill px-4 py-1.5 text-sm font-medium transition-colors',
              tab === t.key ? 'bg-card text-ink shadow-card' : 'text-ink-soft hover:text-ink',
            )}
          >
            <t.icon className="size-4" aria-hidden />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'leaderboard' ? (
        <LeaderboardPanel />
      ) : (
        <>
          <div className="mb-4 flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, -1))} aria-label="Previous day">
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDate(asOf)}>Today</Button>
            <Button size="sm" variant="ghost" onClick={() => setDate(addDays(date, 1))} aria-label="Next day" disabled={date >= asOf}>
              <ChevronRight className="size-4" aria-hidden />
            </Button>
            <span className="ml-1 text-sm text-muted">{DAY_FMT.format(new Date(`${date}T00:00:00`))}{isToday && ' · Today'}</span>
          </div>

          {loading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-card" />)}
            </div>
          ) : isToday ? (
            <>
              <div className="relative mb-3">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search a member to check in…"
                  className="h-touch w-full rounded-control border border-line bg-elevated pl-9 pr-3 text-sm text-ink outline-none focus:border-ink"
                />
              </div>
              {roster.length === 0 ? (
                <EmptyState icon={<UserCheck className="size-6" aria-hidden />} title="No members" description="Add members first, then check them in here." />
              ) : (
                <ul className="flex flex-col gap-2">
                  {roster.map((m) => {
                    const v = checkedIn.get(m.id);
                    return (
                      <li key={m.id}>
                        <Card className="flex items-center justify-between gap-3 p-3">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-ink">{m.fullName}</p>
                            {!hasGymAccess(m.status) && (
                              <p className="text-xs text-muted">
                                {m.status === 'NEVER_PAID' ? 'No gym access yet' : 'Gym access ended'}
                              </p>
                            )}
                          </div>
                          {v ? (
                            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-ok-soft px-3 py-1 text-sm font-medium text-ok-ink">
                              <Check className="size-4" aria-hidden /> In · {time(v.checkedInAt)}
                            </span>
                          ) : (
                            <Button size="sm" onClick={() => check(m)}>
                              <UserCheck className="size-4" aria-hidden /> Check in
                            </Button>
                          )}
                        </Card>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (reg?.visits.length ?? 0) === 0 ? (
            <EmptyState icon={<UserCheck className="size-6" aria-hidden />} title="No check-ins" description="No one was checked in on this day." />
          ) : (
            <ul className="flex flex-col gap-2">
              {(reg?.visits ?? []).map((v) => (
                <li key={v.id}>
                  <Card className="flex items-center justify-between gap-3 p-3">
                    <p className="min-w-0 truncate font-medium text-ink">{v.memberName}</p>
                    <span className="shrink-0 text-sm text-muted">{time(v.checkedInAt)}{v.source === 'SELF' ? ' · self' : ''}</span>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {qrOpen && <CheckInQrModal gymName={settings.name} onClose={() => setQrOpen(false)} />}
      {justIn?.streak && (
        <StreakModal
          name={justIn.memberName}
          already={justIn.alreadyCheckedIn}
          streak={justIn.streak}
          onClose={() => setJustIn(null)}
        />
      )}
    </section>
  );
}

function LeaderboardPanel() {
  const [period, setPeriod] = useState<'WEEK' | 'MONTH'>('WEEK');
  const { data, loading } = useAsync<AttendanceLeaderboard>(() => getAttendanceLeaderboard(period), [period]);

  return (
    <CheckInLeaderboard
      rows={data?.rows ?? []}
      loading={loading}
      period={period}
      onPeriod={setPeriod}
    />
  );
}

function StreakModal({
  name,
  already,
  streak,
  onClose,
}: {
  name: string;
  already: boolean;
  streak: CheckInStreak;
  onClose: () => void;
}) {
  const first = name.split(/\s+/)[0];
  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">
              {already ? `Already in, ${first}` : `${first} is in`}
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              {streak.weekStreak >= 2
                ? `${streak.weekStreak} weeks in a row — that's consistency.`
                : 'Small calendar of this week. Keep showing up.'}
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <StreakWeekCard streak={streak} name={name} />
        <Button onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

/** The gym's printable self check-in QR — points at /checkin on this gym's own host. */
function CheckInQrModal({ gymName, onClose }: { gymName: string; onClose: () => void }) {
  const url = `${window.location.origin}/checkin`;
  const qrRef = useRef<HTMLDivElement>(null);

  // Print a clean, dedicated poster — not the app/modal chrome — by writing a print-only window.
  function print() {
    const svg = qrRef.current?.querySelector('svg')?.outerHTML;
    if (!svg) return;
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(gymName)} — check in</title>
      <style>
        @page { margin: 16mm; }
        html,body { height: 100%; }
        body { margin: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
          text-align: center; font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #15170f; }
        .gym { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
        h1 { font-size: 44px; line-height: 1.1; margin: 8px 0 6px; }
        p { font-size: 20px; color: #555; margin: 0 0 28px; }
        .qr svg { width: 78mm; height: 78mm; }
        .url { margin-top: 24px; font-family: monospace; font-size: 13px; color: #888; }
      </style></head>
      <body>
        <div class="gym">${escapeHtml(gymName)}</div>
        <h1>Scan to check in</h1>
        <p>Point your phone camera at the code, then tap “Check in”.</p>
        <div class="qr">${svg}</div>
        <div class="url">${escapeHtml(url)}</div>
      </body></html>`);
    w.document.close();
    w.focus();
    // Let the SVG paint before the print dialog opens.
    setTimeout(() => w.print(), 300);
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex w-full flex-col items-center gap-3 text-center">
        <div className="flex w-full items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Members check in here</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <p className="text-sm text-muted">Print this and put it at the door. Members scan it with their phone camera and tap to check in.</p>
        <div ref={qrRef} className="rounded-card border border-line bg-white p-4">
          <QRCodeSVG value={url} size={220} marginSize={2} />
        </div>
        <code className="rounded-control bg-surface px-3 py-1.5 text-xs text-ink-soft">{url}</code>
        <Button className="mt-1" onClick={print}>
          <Printer className="size-4" aria-hidden /> Print
        </Button>
      </div>
    </Modal>
  );
}

/** Minimal HTML escape for values dropped into the print window. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
